import type { TRPCLink } from '@trpc/client';
import type { AnyRouter } from '@trpc/server';
import { observable } from '@trpc/server/observable';
import * as Sentry from '@sentry/react';

export interface SentryLinkOptions {
  source: 'school' | 'graph';
  maxInputBytes?: number;
}

export const sentryLink = <TRouter extends AnyRouter>(
  opts: SentryLinkOptions,
): TRPCLink<TRouter> => {
  const maxBytes = opts.maxInputBytes ?? 2000;

  return () => ({ op, next }) => {
    return observable((observer) => {
      const subscription = next(op).subscribe({
        next: (value) => observer.next(value),
        error: (err) => {
          const code = (err as { data?: { code?: string } })?.data?.code;
          const httpStatus = (err as { data?: { httpStatus?: number } })?.data?.httpStatus;

          // Skip expected client errors — these aren't bugs
          if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN' || code === 'NOT_FOUND') {
            observer.error(err);
            return;
          }

          Sentry.captureException(err, {
            tags: {
              trpc_source: opts.source,
              trpc_path: op.path,
              trpc_type: op.type,
              trpc_code: code,
              trpc_http_status: httpStatus,
            },
            contexts: {
              trpc: {
                source: opts.source,
                path: op.path,
                type: op.type,
                input: safeStringifyInput(op.input, maxBytes),
              },
            },
          });
          observer.error(err);
        },
        complete: () => observer.complete(),
      });
      return () => subscription.unsubscribe();
    });
  };
};

const safeStringifyInput = (input: unknown, maxBytes: number): string | undefined => {
  if (input === undefined) return undefined;
  try {
    const s = JSON.stringify(input);
    if (s === undefined) return undefined;
    return s.length > maxBytes ? `${s.slice(0, maxBytes)}…[truncated]` : s;
  } catch {
    return '[unserializable]';
  }
};
